import type { LineBox } from '../../models/layout';

export interface SeamCheckResult {
  ok: boolean;
  pullFromPrev?: number; // lines to pull from previous page top
  pushToNext?: number;   // lines to push to next page
}

export interface SeamPolicy { minPrev: number; minNext: number }

// Paragraph-aware policy: enforce only when the seam splits the same paragraph.
export function checkSeam(prevLines: LineBox[], nextLines: LineBox[], policy: SeamPolicy = { minPrev: 2, minNext: 2 }): SeamCheckResult {
  const MIN_PREV = Math.max(0, policy.minPrev);
  const MIN_NEXT = Math.max(0, policy.minNext);
  if (!prevLines.length || !nextLines.length) return { ok: true };

  const lastPrev = prevLines[prevLines.length - 1];
  const firstNext = nextLines[0];

  // If different paragraphs on either side of the seam, no widow/orphan risk at this seam
  if (lastPrev.blockId !== firstNext.blockId) return { ok: true };

  const pid = lastPrev.blockId;
  const prevCount = countLinesOfParagraph(prevLines, pid);
  const nextCount = countLinesOfParagraph(nextLines, pid);

  const needFromNext = prevCount < MIN_PREV ? (MIN_PREV - prevCount) : 0; // pull from next to increase prev side
  const needToNext = nextCount < MIN_NEXT ? (MIN_NEXT - nextCount) : 0;   // push to next to increase next side

  // Prefer "push whole paragraph" if the previous page contains only this paragraph's lines
  const prevOnlyThisPara = prevLines.every(l => l.blockId === pid);
  if (needFromNext > 0 && needToNext > 0 && prevOnlyThisPara) {
    return { ok: false, pushToNext: prevCount }; // move entire paragraph to next page
  }
  if (needFromNext > 0 && needToNext > 0) return { ok: false, pullFromPrev: needFromNext };
  if (needFromNext > 0) return { ok: false, pullFromPrev: needFromNext };
  if (needToNext > 0) return { ok: false, pushToNext: needToNext };
  return { ok: true };
}

function countLinesOfParagraph(lines: LineBox[], blockId: string): number {
  let n = 0;
  for (const l of lines) if (l.blockId === blockId) n++;
  return n;
}


